import { authGuard } from '@/features/auth/guards/authGuard.js'
import { sessionGuard } from '@/features/auth/guards/sessionGuard.js'
import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { useAppEnvironmentMode } from '@/features/environment/useAppEnvironmentMode.js'
import { superAdminSidebarMenuLinkItems } from '@/features/layout/components/constants/useSuperAdminSidebarMenuLinks.js'
import SuperAdminLayout from '@/features/layout/superAdmin/SuperAdminLayout.vue'
import { createNoAccessPath } from '@/features/noAccess/noAccess.utils.js'
import { decidePermission } from '@/features/permissions/permissionEngine.js'
import { PermissionActions } from '@/features/permissions/permissionActions.js'
import { createProfileManagementRoutes } from '@/features/profileManagement/profileManagementRoutes.js'
import { superAdminClubManagementRoutes } from '@/features/superAdmin/clubManagement/superAdminClubManagementRoutes.js'
import { superAdminLimitedUsersRoutes } from '@/features/superAdmin/limitedUsers/superAdminLimitedUsersRoutes.js'
import { superAdminLogsManagementRoutes } from '@/features/superAdmin/logsManagement/superAdminLogsManagementRoutes.js'
import { masterRoutes } from '@/features/superAdmin/master/masterRoutes.js'
// import { superAdminDashboardRoutes } from '@/features/superAdmin/superAdminDashboard/superAdminDashboardRoutes.js'
import { superAdminGuard } from '@/features/superAdmin/superAdminGuard.js'
import { superAdminRolesRoutes } from '@/features/superAdmin/superAdminRoleManagement/superAdminRolesRoutes.js'
import { SuperAdminLimitedRootViewActions } from '@/features/superAdmin/superAdminLimitedPermissionActions.js'
import { superDashboardRoutes } from '@/features/superAdmin/superDashboard/superDashboardRoutes.js'

import { superAdminPeopleManagementRoutes } from './peopleManagement/superAdminPeopleManagementRoutes'

const { isUat2Mode } = useAppEnvironmentMode()

const rootRedirects = superAdminSidebarMenuLinkItems.flatMap(item => {
  if (item.uat2Only && !isUat2Mode) return []
  if (!item.to) return []

  const actions =
    item.permissionAnyActions || (item.permissionAction ? [item.permissionAction] : [])
  return actions.map(action => [action, item.to])
})

function resolveSuperAdminRootRedirect(to) {
  const authStore = useAuthStore()

  for (const [action, path] of rootRedirects) {
    const decision = decidePermission(action, {
      myDetails: authStore.myDetails,
      surface: 'super-admin',
      route: '/super-admin',
    })

    if (decision.allowed) return path
  }

  return createNoAccessPath({
    reason: 'permission-denied',
    action: 'super-admin-limited',
    returnUrl: to.fullPath,
  })
}

export const superAdminRoutes = [
  {
    path: '/super-admin',
    component: SuperAdminLayout,
    children: [
      {
        path: '',
        beforeEnter: resolveSuperAdminRootRedirect,
        component: () => import('@/features/ui/components/DummyRouter.vue'),
      },
      // ...superAdminDashboardRoutes, Karl said we can use clubs page as home.
      ...superAdminClubManagementRoutes,
      ...superDashboardRoutes,
      ...createProfileManagementRoutes({ accessLevel: 'super-admin' }),
      ...superAdminRolesRoutes,
      ...masterRoutes,
      ...superAdminPeopleManagementRoutes,
      ...superAdminLimitedUsersRoutes,
      {
        path: 'global-site-themes',
        component: () =>
          import('@/features/site/withTemplateEditingMode.js').then(({ withTemplateEditingMode }) =>
            withTemplateEditingMode(
              () => import('@/features/superAdmin/master/globalSiteTemplates/TemplateList.vue'),
            ),
          ),
        meta: {
          permissionAction: PermissionActions.SuperAdminLimitedGlobalSiteThemesView,
        },
      },
      {
        path: 'global-site-themes-new',
        component: () =>
          import('@/features/site/withTemplateEditingMode.js').then(({ withTemplateEditingMode }) =>
            withTemplateEditingMode(
              () => import('@/features/superAdmin/master/globalSiteTemplates/TemplateNew.vue'),
            ),
          ),
        meta: {
          permissionAction: PermissionActions.SuperAdminLimitedGlobalSiteThemesCreate,
        },
      },
      {
        path: 'global-site-themes/:currentSiteIdParam',
        component: () =>
          import('@/features/site/withTemplateEditingMode.js').then(({ withTemplateEditingMode }) =>
            withTemplateEditingMode(() => import('@/features/site/SiteRouterView.vue')),
          ),
        meta: {
          layoutPadding: false,
          permissionAction: PermissionActions.SuperAdminLimitedGlobalSiteThemesView,
        },
        children: [
          {
            path: 'sitemap-builder',
            component: () =>
              import('@/features/site/withTemplateEditingMode.js').then(
                ({ withTemplateEditingMode }) =>
                  withTemplateEditingMode(
                    () => import('@/features/siteMapBuilder/SiteMapBuilder.vue'),
                  ),
              ),
            meta: {
              layoutPadding: false,
              permissionAction: PermissionActions.SuperAdminLimitedGlobalSiteThemesSitemapView,
            },
          },
          {
            path: 'page-builder',
            component: () =>
              import('@/features/site/withTemplateEditingMode.js').then(
                ({ withTemplateEditingMode }) =>
                  withTemplateEditingMode(
                    () => import('@/features/sitePageBuilder/SitePageBuilderLayout.vue'),
                  ),
              ),
            meta: {
              layoutPadding: false,
              permissionAction: PermissionActions.SuperAdminLimitedGlobalSiteThemesPageBuilderView,
            },
            children: [
              {
                path: ':pageId',
                component: () =>
                  import('@/features/sitePageBuilder/SitePageBuilderContent.vue').then(
                    m => m.default,
                  ),
                meta: {
                  layoutPadding: false,
                  permissionAction:
                    PermissionActions.SuperAdminLimitedGlobalSiteThemesPageBuilderView,
                },
              },
            ],
          },
        ],
      },
      ...superAdminLogsManagementRoutes,
    ],
    beforeEnter: [authGuard, sessionGuard, superAdminGuard],
    meta: {
      accessLevel: 'super-admin',
      permissionAnyActions: SuperAdminLimitedRootViewActions,
    },
  },
]
